import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Renders idwiz `formFields` from the JSON API (AI-driven tasks).
 * Always includes a Primary Action row; optional Enter-to-search for OIN catalog fields.
 */
export function isOinSearchField(field) {
  if (!field || typeof field !== "object") return false;
  if (field.submitOnEnter === true) return true;
  if (field.role === "oin_search") return true;
  return false;
}

export default function DynamicForm({
  fields,
  values,
  onChange,
  disabled,
  /** Preferred name for Primary / Enter submit — same as onPrimaryAction if omitted. */
  onSubmit,
  onPrimaryAction,
  primaryActionLabel = "Primary action",
  primaryDisabled = false,
  primaryBusy = false,
  onOinSearchEnter,
  /** Preferred handler name for OIN primary + Enter; falls back to onOinSearchEnter. */
  executeOinSearch = null,
  oinSearchDisabled = false,
  /** Minimum trimmed length before Search OIN / Enter runs (matches fetchOinResults). */
  minOinSearchLength = 3,
  /** Wait this long after typing stops before auto-running OIN search (reduces 429s). */
  oinSearchDebounceMs = 500,
  onNavigateBack,
  /** When set, overrides `disabled` for Cancel / Back only (e.g. allow dismiss while agent streams). */
  navigateBackDisabled,
}) {
  const oinDebounceTimerRef = useRef(null);
  /** True only after the user edits the OIN field via onChange (skips debounced search on mount / prefilled values). */
  const userEditedOinFieldRef = useRef(false);
  /** Last query we actually dispatched to `oinRun` (debounced or manual); avoids duplicate fires for the same string. */
  const lastDispatchedOinQueryRef = useRef(null);

  const oinFieldId = useMemo(() => {
    for (const f of fields || []) {
      if (!isOinSearchField(f)) continue;
      const id = f?.id != null ? String(f.id) : "";
      if (id) return id;
    }
    return "";
  }, [fields]);

  const oinSearchTrimmed = oinFieldId ? String(values[oinFieldId] ?? "").trim() : "";

  const oinRun =
    typeof executeOinSearch === "function" ? executeOinSearch : onOinSearchEnter;

  const oinRunRef = useRef(oinRun);
  oinRunRef.current = oinRun;

  const oinFieldIdRef = useRef(oinFieldId);
  oinFieldIdRef.current = oinFieldId;

  const oinSearchTrimmedRef = useRef(oinSearchTrimmed);
  oinSearchTrimmedRef.current = oinSearchTrimmed;

  const gatesRef = useRef({
    oinFieldId,
    disabled,
    oinSearchDisabled,
    minOinSearchLength,
    oinSearchDebounceMs,
  });
  gatesRef.current = {
    oinFieldId,
    disabled,
    oinSearchDisabled,
    minOinSearchLength,
    oinSearchDebounceMs,
  };

  const handleFieldChange = useCallback(
    (id, val) => {
      if (oinFieldIdRef.current && id === oinFieldIdRef.current) {
        userEditedOinFieldRef.current = true;
      }
      onChange(id, val);
    },
    [onChange],
  );

  /** Stable entry for Enter / primary — reads latest handler + gates from refs. */
  const invokeOinSearch = useCallback((rawQuery) => {
    const g = gatesRef.current;
    const fn = oinRunRef.current;
    if (typeof fn !== "function") return;
    const q = String(rawQuery ?? "").trim();
    if (q.length < g.minOinSearchLength) return;
    if (g.disabled || g.oinSearchDisabled) return;
    lastDispatchedOinQueryRef.current = q;
    fn(q);
  }, []);

  /**
   * Debounced auto-search: depends only on the trimmed query string.
   * Gates, handler, and busy flags are read from refs so parent re-renders do not reschedule.
   */
  useEffect(() => {
    const g = gatesRef.current;
    const q = String(oinSearchTrimmed).trim();

    if (!g.oinFieldId || g.oinSearchDebounceMs <= 0) return;

    if (q.length < g.minOinSearchLength) {
      lastDispatchedOinQueryRef.current = null;
      return;
    }

    if (!userEditedOinFieldRef.current) return;

    if (g.disabled || g.oinSearchDisabled || typeof oinRunRef.current !== "function") return;

    if (q === lastDispatchedOinQueryRef.current) return;

    if (oinDebounceTimerRef.current) clearTimeout(oinDebounceTimerRef.current);
    oinDebounceTimerRef.current = setTimeout(() => {
      oinDebounceTimerRef.current = null;
      const g2 = gatesRef.current;
      const latest = String(oinSearchTrimmedRef.current ?? "").trim();
      if (latest.length < g2.minOinSearchLength) return;
      if (!userEditedOinFieldRef.current) return;
      if (g2.disabled || g2.oinSearchDisabled || typeof oinRunRef.current !== "function") return;
      if (latest === lastDispatchedOinQueryRef.current) return;
      lastDispatchedOinQueryRef.current = latest;
      oinRunRef.current(latest);
    }, g.oinSearchDebounceMs);

    return () => {
      if (oinDebounceTimerRef.current) {
        clearTimeout(oinDebounceTimerRef.current);
        oinDebounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only user-driven query changes should reschedule debounce
  }, [oinSearchTrimmed]);

  if (!Array.isArray(fields) || !fields.length) return null;
  const primarySubmit =
    typeof onSubmit === "function" ? onSubmit : typeof onPrimaryAction === "function" ? onPrimaryAction : null;
  const oinSearchEligible =
    Boolean(oinSearchTrimmed) &&
    oinSearchTrimmed.length >= minOinSearchLength &&
    typeof oinRun === "function" &&
    !oinSearchDisabled &&
    !disabled;
  const canPrimaryExecute = typeof primarySubmit === "function";
  const primaryButtonDisabled =
    primaryBusy ||
    (!oinSearchEligible && (disabled || primaryDisabled || !canPrimaryExecute));
  const backButtonDisabled = navigateBackDisabled != null ? navigateBackDisabled : disabled;

  function gatherFormData() {
    const out = {};
    for (const f of fields) {
      const id = f?.id != null ? String(f.id) : "";
      if (!id) continue;
      const isSel = f.type === "select" && Array.isArray(f.options) && f.options.length > 0;
      const isRadio = f.type === "radio" && Array.isArray(f.options) && f.options.length > 0;
      const firstOpt = isSel || isRadio
        ? (typeof f.options[0] === "string" ? f.options[0] : String(f.options[0]?.value ?? f.options[0]?.label ?? ""))
        : "";
      const raw = values[id];
      if (f.type === "checkbox") {
        out[id] = Boolean(raw);
      } else if (isSel || isRadio) {
        out[id] = raw != null && raw !== "" ? raw : firstOpt;
      } else {
        out[id] = raw ?? "";
      }
    }
    return out;
  }

  function clearOinDebounce() {
    if (oinDebounceTimerRef.current) {
      clearTimeout(oinDebounceTimerRef.current);
      oinDebounceTimerRef.current = null;
    }
  }

  function handlePrimaryClick() {
    if (oinSearchEligible) {
      clearOinDebounce();
      invokeOinSearch(oinSearchTrimmed);
      return;
    }
    const formValues = gatherFormData();
    if (typeof primarySubmit === "function") {
      // eslint-disable-next-line no-console -- triage primary action wiring
      console.log("[DynamicForm] Primary action clicked", formValues);
      primarySubmit(formValues);
    }
  }

  return (
    <div className="space-y-3 rounded-xl border border-[rgb(var(--idaxis-accent-primary-rgb)/0.2)] bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Your entries</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fields.map((f) => {
          const id = f.id != null ? String(f.id) : "";
          if (!id) return null;
          const label = f.label != null ? String(f.label) : id;
          const type =
            f.type === "email" ? "email" : f.type === "password" ? "password" : "text";
          const isTextarea = f.type === "textarea";
          const required = Boolean(f.required);
          const oinSearch = isOinSearchField(f);
          const isSelect = f.type === "select" && Array.isArray(f.options) && f.options.length > 0;
          const isRadio = f.type === "radio" && Array.isArray(f.options) && f.options.length > 0;
          const isCheckbox = f.type === "checkbox";
          const firstOptionVal = isSelect || isRadio
            ? (() => {
                const opt = f.options[0];
                return typeof opt === "string" ? opt : String(opt?.value ?? opt?.label ?? "");
              })()
            : "";

          if (isRadio) {
            const currentVal = values[id] != null && values[id] !== "" ? values[id] : firstOptionVal;
            return (
              <div key={id} className="block sm:col-span-2">
                <span className="text-[11px] text-slate-500">
                  {label}
                  {required ? <span className="text-rose-400/90"> *</span> : null}
                </span>
                <div className="mt-2 space-y-2">
                  {f.options.map((opt) => {
                    const v = typeof opt === "string" ? opt : String(opt?.value ?? opt?.label ?? "");
                    const lab = typeof opt === "string" ? opt : String(opt?.label ?? opt?.value ?? "");
                    const inputId = `${id}-${v}`;
                    return (
                      <label
                        key={v || lab}
                        htmlFor={inputId}
                        className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-[#050810]/80 px-3 py-2 text-sm text-slate-200"
                      >
                        <input
                          id={inputId}
                          type="radio"
                          name={id}
                          value={v}
                          checked={String(currentVal) === v}
                          onChange={() => handleFieldChange(id, v)}
                          disabled={disabled}
                          className="h-4 w-4 accent-[var(--idaxis-accent-primary)]"
                        />
                        <span>{lab}</span>
                      </label>
                    );
                  })}
                </div>
                {f.helpText != null && String(f.helpText).trim() ? (
                  <p className="mt-1 text-[11px] text-slate-500">{String(f.helpText)}</p>
                ) : null}
              </div>
            );
          }

          if (isCheckbox) {
            return (
              <label key={id} className="flex cursor-pointer items-start gap-3 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(values[id])}
                  onChange={(e) => handleFieldChange(id, e.target.checked)}
                  disabled={disabled}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] accent-[var(--idaxis-accent-primary)]"
                />
                <span className="text-[13px] leading-snug text-slate-200">
                  {label}
                  {required ? <span className="text-rose-400/90"> *</span> : null}
                  {f.helpText != null && String(f.helpText).trim() ? (
                    <span className="mt-1 block text-[11px] font-normal text-slate-500">{String(f.helpText)}</span>
                  ) : null}
                </span>
              </label>
            );
          }

          return (
            <label key={id} className="block sm:col-span-2">
              <span className="text-[11px] text-slate-500">
                {label}
                {required ? <span className="text-rose-400/90"> *</span> : null}
              </span>
              {isSelect ? (
                <>
                  <select
                    value={values[id] != null && values[id] !== "" ? values[id] : firstOptionVal}
                    onChange={(e) => handleFieldChange(id, e.target.value)}
                    disabled={disabled}
                    className="mt-1 w-full cursor-pointer appearance-none rounded-lg border border-white/10 bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] px-3 py-2.5 text-sm text-slate-200 focus:border-[rgb(var(--idaxis-accent-primary-rgb)/0.4)] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--idaxis-accent-primary-rgb)/0.3)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {f.options.map((opt) => {
                      const v = typeof opt === "string" ? opt : String(opt?.value ?? opt?.label ?? "");
                      const lab = typeof opt === "string" ? opt : String(opt?.label ?? opt?.value ?? "");
                      return (
                        <option key={v || lab} value={v}>
                          {lab}
                        </option>
                      );
                    })}
                  </select>
                  {f.helpText != null && String(f.helpText).trim() ? (
                    <p className="mt-1 text-[11px] text-slate-500">{String(f.helpText)}</p>
                  ) : null}
                </>
              ) : isTextarea ? (
                <textarea
                  value={values[id] ?? ""}
                  onChange={(e) => handleFieldChange(id, e.target.value)}
                  disabled={disabled}
                  rows={4}
                  autoComplete="off"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[rgb(var(--idaxis-accent-primary-rgb)/0.4)] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--idaxis-accent-primary-rgb)/0.3)] disabled:opacity-50"
                  placeholder={f.placeholder != null ? String(f.placeholder) : ""}
                />
              ) : (
                <input
                  type={type}
                  value={values[id] ?? ""}
                  onChange={(e) => handleFieldChange(id, e.target.value)}
                  onKeyDown={(e) => {
                    if (!oinSearch || typeof oinRun !== "function") return;
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    const q = String(values[id] ?? "").trim();
                    if (q.length < minOinSearchLength || oinSearchDisabled || disabled) return;
                    clearOinDebounce();
                    invokeOinSearch(q);
                  }}
                  disabled={disabled}
                  autoComplete="off"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[rgb(var(--idaxis-accent-primary-rgb)/0.4)] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--idaxis-accent-primary-rgb)/0.3)] disabled:opacity-50"
                  placeholder={f.placeholder != null ? String(f.placeholder) : ""}
                />
              )}
            </label>
          );
        })}
      </div>

      {fields.some(isOinSearchField) && oinSearchTrimmed && oinSearchTrimmed.length < minOinSearchLength ? (
        <p className="text-[11px] text-slate-500">Type at least {minOinSearchLength} characters to search.</p>
      ) : null}

      <div className="border-t border-[rgb(var(--idaxis-accent-primary-rgb)/0.15)] pt-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <button
            type="button"
            onClick={handlePrimaryClick}
            disabled={primaryButtonDisabled}
            className="min-h-[42px] flex-1 rounded-lg bg-[rgb(var(--idaxis-accent-primary-rgb)/1)] py-2.5 text-sm font-bold text-[var(--idaxis-bg-primary)] shadow-[0_0_16px_rgb(var(--idaxis-accent-primary-rgb)/0.28)] transition hover:shadow-[0_0_24px_rgb(var(--idaxis-accent-primary-rgb)/0.4)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {primaryBusy
              ? primaryActionLabel === "Searching…" || primaryActionLabel === "Creating…"
                ? primaryActionLabel
                : "Working…"
              : primaryActionLabel}
          </button>
          {typeof onNavigateBack === "function" ? (
            <button
              type="button"
              onClick={() => onNavigateBack()}
              disabled={backButtonDisabled}
              className="shrink-0 rounded-lg border border-white/15 bg-white/[0.04] px-4 py-2.5 text-center text-[13px] font-medium text-slate-200 transition hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-40 sm:min-w-[9rem]"
            >
              Cancel / Back
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
